import {
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FileClock,
  RefreshCw,
  SearchX,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { KycRecord } from "@/types/kyc";
import { DEFAULT_RANGE, inRange } from "@/pages/retailer/wallet/data";
import { formatDate, formatTime } from "@/pages/admin/data";
import KycStatusBadge from "./components/KycStatusBadge";
import StatusTabs, { type KycTab } from "./components/StatusTabs";
import FilterBar, { type FilterChip } from "./components/FilterBar";
import { initials, requiredDocCount, requiredUploadedCount } from "./data";
import { kycService } from "@/services/apiServices";

function KycStatCard({
  label,
  value,
  icon: Icon,
  iconBg,
  iconColor,
  sub,
  onClick,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  sub: string;
  onClick?: () => void;
}) {
  const isClickable = !!onClick;
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-[10px] border border-line bg-card p-5 shadow-card transition-all ${
        isClickable
          ? "cursor-pointer hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0"
          : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] text-ink-secondary">{label}</span>
          <span className="text-[30px] font-bold leading-tight text-ink">{value}</span>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg} ${iconColor}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="mt-3 text-xs text-ink-muted">{sub}</p>
    </div>
  );
}

function escapeCsvValue(value: unknown): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(rows: (string | number | null | undefined)[][], filename: string) {
  const content = rows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AdminKyc() {
  return (
    <ToastProvider>
      <KycInner />
    </ToastProvider>
  );
}

function KycInner() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [records, setRecords] = useState<KycRecord[]>([]);
  const [tab, setTab] = useState<KycTab>("pending");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [sort, setSort] = useState("Newest First");

  const fetchRecords = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await kycService.getAdminList({
        search: search.trim(),
        status: "",
        businessType: type,
        sort,
      });

      if (res?.data?.records && Array.isArray(res.data.records)) {
        setRecords(res.data.records);
      }
      if (typeof res?.data?.counts?.pending === "number") {
        window.dispatchEvent(
          new CustomEvent("mandatepro_kyc_badge_updated", {
            detail: { pendingCount: res.data.counts.pending },
          })
        );
      }
    } catch (err: any) {
      console.warn("Error fetching admin KYC list from API:", err?.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search, type, sort]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const stats = useMemo(() => {
    const pending = records.filter((r) => r.status === "SUBMITTED" || r.status === "UNDER_REVIEW").length;
    const approved = records.filter((r) => r.status === "APPROVED").length;
    const rejected = records.filter((r) => r.status === "REJECTED").length;
    const notSubmitted = records.filter((r) => r.status === "NOT_STARTED" || r.status === "DRAFT").length;
    const submittedApplications = pending + approved + rejected;
    const approvedPct = submittedApplications ? Math.round((approved / submittedApplications) * 100) : 0;
    return { pending, approved, rejected, notSubmitted, approvedPct };
  }, [records]);

  const counts: Record<KycTab, number> = useMemo(
    () => ({
      all: records.length,
      pending: stats.pending,
      approved: stats.approved,
      rejected: stats.rejected,
      not: stats.notSubmitted,
    }),
    [records, stats]
  );

  const filtered = useMemo(() => {
    let rows = records.slice();

    if (tab === "pending") rows = rows.filter((r) => r.status === "SUBMITTED" || r.status === "UNDER_REVIEW");
    if (tab === "approved") rows = rows.filter((r) => r.status === "APPROVED");
    if (tab === "rejected") rows = rows.filter((r) => r.status === "REJECTED");
    if (tab === "not") rows = rows.filter((r) => r.status === "NOT_STARTED" || r.status === "DRAFT");

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const name = r.retailerName || r.ownerFullName || "";
        const shop = r.shopName || r.legalBusinessName || "";
        return `${r.retailerId} ${r.legalBusinessName} ${r.businessPan} ${shop} ${name}`
          .toLowerCase()
          .includes(q);
      });
    }
    if (type) rows = rows.filter((r) => r.businessType === type);

    // Only filter date range if a date exists or range is customized
    if (tab !== "not" && range && range !== "All Time") {
      rows = rows.filter((r) => (r.submittedAt ? inRange(r.submittedAt, range, "", "") : true));
    }

    if (sort === "Newest First") {
      rows.sort((a, b) => (b.submittedAt ?? "").localeCompare(a.submittedAt ?? ""));
    } else {
      rows.sort((a, b) => (a.submittedAt ?? "").localeCompare(b.submittedAt ?? ""));
    }
    return rows;
  }, [records, tab, search, type, range, sort]);

  const chips: FilterChip[] = useMemo(() => {
    const c: FilterChip[] = [];
    if (search.trim()) c.push({ label: `Search: ${search}`, onRemove: () => setSearch("") });
    if (type) c.push({ label: `Type: ${type}`, onRemove: () => setType("") });
    if (range !== DEFAULT_RANGE) c.push({ label: `Date: ${range}`, onRemove: () => setRange(DEFAULT_RANGE) });
    if (sort !== "Newest First") c.push({ label: `Sort: ${sort}`, onRemove: () => setSort("Newest First") });
    return c;
  }, [search, type, range, sort]);

  function resetFilters() {
    setSearch("");
    setType("");
    setRange(DEFAULT_RANGE);
    setSort("Newest First");
  }

  function handleExport() {
    if (filtered.length === 0) {
      toast.error("No KYC applications to export");
      return;
    }
    setExporting(true);
    try {
      const headers = [
        "Application ID",
        "Retailer ID",
        "Retailer Name",
        "Shop / Trade Name",
        "Legal Business Name",
        "Business Type",
        "Business PAN",
        "GST Number",
        "Mobile",
        "Email",
        "Status",
        "Documents Uploaded",
        "Required Documents",
        "Submitted On",
        "Approved On",
        "Rejection Reason",
      ];
      const rows = filtered.map((r) => [
        r.id || r.applicationId || "—",
        r.retailerId || "—",
        r.retailerName || r.ownerFullName || "—",
        r.shopName || r.tradeName || "—",
        r.legalBusinessName || "—",
        r.businessType || "—",
        r.businessPan || "—",
        r.gstNumber || "—",
        r.businessMobile || r.mobile || "—",
        r.businessEmail || "—",
        r.status || "—",
        requiredUploadedCount(r),
        requiredDocCount(r.businessType),
        r.submittedAt ? formatDate(r.submittedAt) : "—",
        r.approvedAt ? formatDate(r.approvedAt) : "—",
        r.rejectionReason || "—",
      ]);
      downloadCsv([headers, ...rows], `kyc-applications-${Date.now()}.csv`);
      toast.success(`Exported ${filtered.length} KYC application${filtered.length === 1 ? "" : "s"}`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to export KYC data");
    } finally {
      setExporting(false);
    }
  }

  const columns: Column<KycRecord>[] = [
    {
      key: "retailerId",
      header: "Retailer",
      render: (row) => {
        const name = row.retailerName || row.ownerFullName || "—";
        const shopName = row.shopName || row.tradeName || row.legalBusinessName || "—";
        return (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-[13px] font-semibold text-[#1E40AF]">
              {initials(name)}
            </span>
            <div>
              <p className="text-sm font-medium text-ink">{shopName}</p>
              <p className="text-xs text-ink-secondary">
                {name} · {row.retailerId}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: "legalBusinessName",
      header: "Business Name",
      render: (row) => (
        <div>
          <p className="text-sm text-ink">{row.legalBusinessName || "—"}</p>
          <p className="text-xs text-ink-secondary">{row.businessType ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "businessPan",
      header: "Business PAN",
      render: (row) => <span className="font-mono text-[13px] text-ink">{row.businessPan || "—"}</span>,
    },
    {
      key: "documents",
      header: "Documents",
      render: (row) => {
        const uploaded = requiredUploadedCount(row);
        const required = requiredDocCount(row.businessType);
        const incomplete = required > 0 && uploaded < required;
        return (
          <div>
            <p className={`text-sm font-medium ${incomplete ? "text-[#DC2626]" : "text-ink"}`}>{uploaded}</p>
            <p className={`text-xs ${incomplete ? "text-[#DC2626]" : "text-ink-secondary"}`}>
              of {required} required
            </p>
          </div>
        );
      },
    },
    {
      key: "submittedAt",
      header: "Submitted On",
      render: (row) =>
        row.submittedAt ? (
          <div>
            <p className="text-[13px] text-ink">{formatDate(row.submittedAt)}</p>
            <p className="text-xs text-ink-secondary">{formatTime(row.submittedAt)}</p>
          </div>
        ) : (
          <span className="text-[#94A3B8]">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <KycStatusBadge status={row.status} />,
    },
  ];

  function rowActions(row: KycRecord) {
    const reviewable = row.status === "SUBMITTED" || row.status === "UNDER_REVIEW";
    return reviewable ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/admin/kyc/${row.retailerId}`);
        }}
        className="inline-flex h-8 items-center whitespace-nowrap rounded-md bg-primary-500 px-3 text-[13px] font-medium text-white hover:bg-primary-600 cursor-pointer"
      >
        Review
      </button>
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/admin/kyc/${row.retailerId}`);
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
        aria-label="View application"
      >
        <Eye className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">KYC Verification</h1>
          <p className="mt-1 text-sm text-ink-secondary">Review and verify retailer KYC applications</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => fetchRecords(true)}
            loading={refreshing}
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={handleExport}
            loading={exporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KycStatCard
            label="Pending Review"
            value={`${stats.pending}`}
            icon={Clock}
            iconBg="bg-[#FFFBEB]"
            iconColor="text-[#D97706]"
            sub="Awaiting your action"
            onClick={() => setTab("pending")}
          />
          <KycStatCard
            label="Approved"
            value={`${stats.approved}`}
            icon={CheckCircle2}
            iconBg="bg-[#ECFDF5]"
            iconColor="text-[#059669]"
            sub={`${stats.approvedPct}% of submitted applications`}
            onClick={() => setTab("approved")}
          />
          <KycStatCard
            label="Rejected"
            value={`${stats.rejected}`}
            icon={XCircle}
            iconBg="bg-[#FEF2F2]"
            iconColor="text-[#DC2626]"
            sub="Accounts closed permanently"
            onClick={() => setTab("rejected")}
          />
          <KycStatCard
            label="Not Submitted"
            value={`${stats.notSubmitted}`}
            icon={FileClock}
            iconBg="bg-[#F1F5F9]"
            iconColor="text-[#475569]"
            sub="Retailers yet to apply"
            onClick={() => setTab("not")}
          />
        </div>

        <StatusTabs tab={tab} onChange={setTab} counts={counts} />

        <FilterBar
          search={search}
          onSearch={setSearch}
          type={type}
          onType={setType}
          range={range}
          onRange={setRange}
          rangeOpen={rangeOpen}
          onRangeOpen={setRangeOpen}
          sort={sort}
          onSort={setSort}
          onReset={resetFilters}
          chips={chips}
        />

        {loading ? (
          <DataTable columns={columns} data={[]} rowKey={(r) => r.retailerId || r.id} loading />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title="No applications found"
            description="Try adjusting your filters or selecting a different tab."
            actionLabel="Reset Filters"
            onAction={resetFilters}
          />
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            rowKey={(r) => r.retailerId || r.id}
            actions={rowActions}
            onRowClick={(row) => navigate(`/admin/kyc/${row.retailerId}`)}
            pageSize={10}
            pageSizeOptions={[10, 25, 50]}
          />
        )}
      </div>
    </div>
  );
}